import React, { useRef, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiMenu } from 'react-icons/fi';
import { useI18n } from '../utilities/i18n';
const criterionLabel = (item, t) => {
  const key = `trTiebreaker_${item.code}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  return item.name || item.code;
};

export default function SeasonTiebreakers({
  items,
  onItemsChange,
  onApplyPreset,
  onReset,
  disabled,
  dirty,
  sendNull,
}) {
  const { t } = useI18n();
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const dragIndexRef = useRef(null);

  const moveRow = (from, to) => {
    if (from === to || to < 0 || to >= items.length) return;
    const next = items.slice();
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    onItemsChange(next);
  };

  const toggleEnabled = (index) => {
    const next = items.map((item, i) => (
      i === index ? { ...item, enabled: !item.enabled } : item
    ));
    onItemsChange(next);
  };

  const handleDragStart = (index, event) => {
    if (disabled) return;
    dragIndexRef.current = index;
    setDragIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (index, event) => {
    if (disabled) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (overIndex !== index) setOverIndex(index);
  };

  const handleDrop = (index, event) => {
    if (disabled) return;
    event.preventDefault();
    const from = dragIndexRef.current;
    if (from != null) moveRow(from, index);
    dragIndexRef.current = null;
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <div className="tr-tiebreakers">
      <div className="tr-tiebreakers-head">
        <label>{t('trTiebreakers')}</label>
        {dirty || sendNull ? (
          <span className="sr-dirty">{t('trUnsaved')}</span>
        ) : null}
      </div>
      <span className="st-photo-hint">{t('trTiebreakersHint')}</span>

      <div className="tr-tiebreakers-presets">
        <button
          type="button"
          className="st-btn st-btn-secondary"
          disabled={disabled}
          onClick={() => onApplyPreset(1)}
        >
          {t('trTiebreakersPresetH2h')}
        </button>
        <button
          type="button"
          className="st-btn st-btn-secondary"
          disabled={disabled}
          onClick={() => onApplyPreset(2)}
        >
          {t('trTiebreakersPresetGoalDiff')}
        </button>
        <button
          type="button"
          className="st-btn st-btn-secondary"
          disabled={disabled}
          onClick={() => onReset()}
        >
          {t('trTiebreakersReset')}
        </button>
      </div>

      <div className={`tr-tiebreakers-list ${disabled ? 'is-disabled' : ''}`.trim()}>
        {items.map((item, index) => (
          <div
            key={item.code}
            className={[
              'tr-tiebreakers-row',
              item.enabled ? 'is-enabled' : 'is-disabled-item',
              dragIndex === index ? 'is-dragging' : '',
              overIndex === index && dragIndex !== index ? 'is-over' : '',
            ].filter(Boolean).join(' ')}
            draggable={!disabled}
            onDragStart={(event) => handleDragStart(index, event)}
            onDragOver={(event) => handleDragOver(index, event)}
            onDrop={(event) => handleDrop(index, event)}
            onDragEnd={handleDragEnd}
          >
            <span className="tr-tiebreakers-handle" aria-hidden="true">
              <FiMenu />
            </span>
            <label className="st-check tr-tiebreakers-check">
              <input
                type="checkbox"
                checked={item.enabled}
                disabled={disabled}
                onChange={() => toggleEnabled(index)}
              />
              <span className="tr-tiebreakers-index">{index + 1}</span>
              <span className="tr-tiebreakers-name">{criterionLabel(item, t)}</span>
            </label>
            <div className="tr-tiebreakers-move">
              <button
                type="button"
                className="st-icon-btn"
                disabled={disabled || index === 0}
                onClick={() => moveRow(index, index - 1)}
                aria-label={t('stMoveUp')}
                title={t('stMoveUp')}
              >
                <FiChevronUp />
              </button>
              <button
                type="button"
                className="st-icon-btn"
                disabled={disabled || index === items.length - 1}
                onClick={() => moveRow(index, index + 1)}
                aria-label={t('stMoveDown')}
                title={t('stMoveDown')}
              >
                <FiChevronDown />
              </button>
            </div>
          </div>
        ))}
      </div>
      <span className="st-photo-hint">{t('trTiebreakersDragHint')}</span>
    </div>
  );
}
